import React, { useState } from 'react';
import { Card, Button, Form, Spinner } from 'react-bootstrap';
import { LuEye, LuEyeOff, LuLock } from 'react-icons/lu';
import { useDispatch, useSelector } from 'react-redux';
import { updatePassword } from '../../../../redux/reducers/authSlice';
import { useFormik } from 'formik';
import * as Yup from 'yup';

const Security = () => {
    const dispatch = useDispatch();
    const loading = useSelector((state) => state.auth.loading);
    const [showPass, setShowPass] = useState({ old: false, new: false });

    // Formik for Password Change
    const passwordFormik = useFormik({
        initialValues: { oldPassword: '', newPassword: '', confirmPassword: '' },
        validationSchema: Yup.object({
            oldPassword: Yup.string().required('Required'),
            newPassword: Yup.string()
                .min(8, 'Minimum 8 characters')
                .required('Required'),
            confirmPassword: Yup.string()
                .oneOf([Yup.ref('newPassword'), null], 'Passwords must match')
                .required('Required'),
        }),
        onSubmit: async (values, { resetForm }) => {
            const res = await dispatch(updatePassword({
                oldPassword: values.oldPassword,
                newPassword: values.newPassword
            }));

            if (res.meta.requestStatus === 'fulfilled') {
                resetForm();
            }
        }
    });

    const togglePass = (field) => setShowPass(prev => ({ ...prev, [field]: !prev[field] }));

    return (
        <Card className="border-0 shadow-sm rounded-4 p-4 mt-3" style={{ maxWidth: '600px' }}>
            <h5 className="fw-bold mb-3">Update Password</h5>
            <p className="text-muted small mb-4">Ensure your account is using a long, random password to stay secure.</p>
            
            <Form onSubmit={passwordFormik.handleSubmit}>
                <Form.Group className="mb-3">
                    <Form.Label className="small fw-bold">Current Password <span className="text-danger">*</span></Form.Label>
                    <div className="position-relative">
                        <Form.Control
                            type={showPass.old ? "text" : "password"}
                            className="bg-light border-0 py-2 shadow-none"
                            {...passwordFormik.getFieldProps('oldPassword')}
                            isInvalid={passwordFormik.touched.oldPassword && !!passwordFormik.errors.oldPassword}
                        />
                        <div
                            className="position-absolute top-50 end-0 translate-middle-y me-3 cursor-pointer text-muted"
                            onClick={() => togglePass('old')}
                        >
                            {showPass.old ? <LuEyeOff size={18} /> : <LuEye size={18} />}
                        </div>
                    </div>
                    <Form.Control.Feedback type="invalid">
                        {passwordFormik.errors.oldPassword}
                    </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label className="small fw-bold">New Password <span className="text-danger">*</span></Form.Label>
                    <div className="position-relative">
                        <Form.Control
                            type={showPass.new ? "text" : "password"}
                            className="bg-light border-0 py-2 shadow-none"
                            {...passwordFormik.getFieldProps('newPassword')}
                            isInvalid={passwordFormik.touched.newPassword && !!passwordFormik.errors.newPassword}
                        />
                        <div
                            className="position-absolute top-50 end-0 translate-middle-y me-3 cursor-pointer text-muted"
                            onClick={() => togglePass('new')}
                        >
                            {showPass.new ? <LuEyeOff size={18} /> : <LuEye size={18} />}
                        </div>
                    </div>
                    <Form.Control.Feedback type="invalid">
                        {passwordFormik.errors.newPassword}
                    </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-4">
                    <Form.Label className="small fw-bold">Confirm New Password <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                        type="password"
                        className="bg-light border-0 py-2 shadow-none"
                        {...passwordFormik.getFieldProps('confirmPassword')}
                        isInvalid={passwordFormik.touched.confirmPassword && !!passwordFormik.errors.confirmPassword}
                    />
                    <Form.Control.Feedback type="invalid">
                        {passwordFormik.errors.confirmPassword}
                    </Form.Control.Feedback>
                </Form.Group>

                <div className="text-end">
                    <Button
                        variant="primary"
                        type="submit"
                        className="rounded-pill px-4 fw-bold"
                        disabled={loading}
                    >
                        {loading ? <Spinner size="sm" /> : (
                            <span className="d-flex align-items-center justify-content-center gap-2">
                                <LuLock size={18} /> Save New Password
                            </span>
                        )}
                    </Button>
                </div>
            </Form>
        </Card>
    );
}

export default Security;
